import {
  Box,
  Button,
  Collapse,
  Flex,
  Heading,
  IconButton,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";
import { IconBlueChecked } from "components/icons/Icons";
import { loadStripe, Stripe, StripeCardElement } from "@stripe/stripe-js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { axiosService } from "network/axios";
import { setAlertMessageRequest } from "store/ui/reducer";
import { getCardsRequest, removeCardInfoRequest } from "store/user/reducer";
import { cardsSelector, userSelector } from "store/user/selector";
import { uploadProjectSelector } from "store/customerProject/selector";
import {
  updateProjectRequest,
  updateProjectStatus,
} from "store/customerProject/reducer";
import { requestDownloadDocumentById } from "store/project/reducer";
import { SelectedProject } from "../types";
import { ServiceOption, getEffectivePrice } from "../constants/services";
import AddNewCardToggle from "views/customer/order/components/AddNewCardToggle";
import NewCardForm from "views/customer/order/components/NewCardForm";
import SavedCardsList from "views/customer/order/components/SavedCardsList";

export type ProjectOverviewCardProps = {
  selectedProject: SelectedProject | null;
  includedAddons: ServiceOption[];
  typeAddons: ServiceOption[];
  documents: any[];
  hasAttachments: boolean;
  onDownloadFiles: (type: string) => void;
  onOpenRevisionModal: () => void;
  onRefreshProject: () => void;
  trackingDetails: any;
};

export default function ProjectOverviewCard({
  selectedProject,
  includedAddons,
  typeAddons,
  documents,
  hasAttachments,
  onDownloadFiles,
  onOpenRevisionModal,
  onRefreshProject,
  trackingDetails,
}: ProjectOverviewCardProps) {
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const user = useSelector(userSelector);
  const cards = useSelector(cardsSelector);
  const uploadProject = useSelector(uploadProjectSelector);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [openCardInputForm, setOpenCardInputForm] = useState(false);
  const [selectedPaymentInfo, setSelectedPaymentInfo] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<ServiceOption | null>(
    null,
  );
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [cardElement, setCardElement] = useState<StripeCardElement | null>(
    null,
  );
  const [cardHolderName, setCardHolderName] = useState("");
  const [cardBrand, setCardBrand] = useState("");
  const stripeCleanupRef = useRef<null | (() => void)>(null);
  const [shippingRecipient, setShippingRecipient] = useState("");
  const [shippingStreet, setShippingStreet] = useState("");
  const [shippingCity, setShippingCity] = useState("");
  const [shippingZip, setShippingZip] = useState("");
  const [shippingState, setShippingState] = useState("");
  const [shippingCountry, setShippingCountry] = useState("");

  useEffect(() => {
    if (user?.user_id) {
      dispatch(getCardsRequest({ user_id: user.user_id }));
    }
  }, [dispatch, user?.user_id]);

  useEffect(() => {
    if (cards?.length < 1) {
      setOpenCardInputForm(true);
    } else {
      setOpenCardInputForm(false);
    }
  }, [cards]);

  useEffect(() => {
    if (openCardInputForm && isOpen) {
      setSelectedPaymentInfo(null);
      dispatch(
        updateProjectStatus({
          saveCardAsDefault: false,
          paymentOption: null,
        }),
      );
      initStripe();
    } else {
      if (stripeCleanupRef.current) {
        stripeCleanupRef.current();
        stripeCleanupRef.current = null;
      }
      setStripe(null);
      setCardElement(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCardInputForm, isOpen]);

  const getDocumentOriginalName = useCallback((doc: any): string => {
    const name =
      doc?.origin_file?.document_original_file_name ||
      doc?.origin_file?.original_file_name ||
      doc?.origin_file?.file_name ||
      doc?.document_original_file_name ||
      doc?.file_name ||
      "";
    return String(name || "");
  }, []);

  const getDocumentFileUrl = useCallback(
    (doc: any): string | null => {
      const document_file_path =
        doc?.origin_file?.document_file_path ||
        doc?.origin_file?.file_path ||
        doc?.document_file_path ||
        null;
      const originalName = getDocumentOriginalName(doc);
      if (!document_file_path) return null;

      if (
        String(document_file_path).includes("http://") ||
        String(document_file_path).includes("https://")
      ) {
        return String(document_file_path);
      }

      if (!originalName) return null;
      // URL-encode the filename so characters like '/', '#', '?', spaces don't break the URL.
      return `${process.env.REACT_APP_API_BASE_URL}/${document_file_path}/${encodeURIComponent(originalName)}`;
    },
    [getDocumentOriginalName],
  );

  const openDocumentInNewTab = useCallback(
    (doc: any) => {
      const url = getDocumentFileUrl(doc);
      if (!url) return;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [getDocumentFileUrl],
  );

  const downloadDocumentFile = useCallback(
    (doc: any) => {
      const url = getDocumentFileUrl(doc);
      if (!url) return;

      const fileName = getDocumentOriginalName(doc) || "file";
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    },
    [getDocumentFileUrl, getDocumentOriginalName],
  );

  const getTranslatedFileName = useCallback((doc: any): string => {
    const name =
      doc?.lastest_file?.document_original_file_name ||
      doc?.lastest_file?.original_file_name ||
      doc?.lastest_file?.file_name ||
      "";
    return String(name || "");
  }, []);

  const hasTranslatedFileForDoc = useCallback(
    (doc: any): boolean => Boolean(doc?.lastest_file?.document_file_path),
    [],
  );

  // Customer-side translated downloads go through the PHP API endpoint
  // (/projects/download/document/{id}) instead of direct nginx URLs.
  // Files are saved outside the nginx webroot, so PHP must stream them.
  const downloadTranslatedFile = useCallback(
    (doc: any) => {
      const documentId = doc?.document_id;
      if (!documentId) return;
      const fileName = getTranslatedFileName(doc) || `document-${documentId}`;
      dispatch(
        requestDownloadDocumentById({
          document_id: documentId,
          file_name: fileName,
        }),
      );
    },
    [dispatch, getTranslatedFileName],
  );

  const hasTranslatedFiles = useMemo(
    () => Array.isArray(documents) && documents.some(hasTranslatedFileForDoc),
    [documents, hasTranslatedFileForDoc],
  );

  const initStripe = async () => {
    if (stripeCleanupRef.current) {
      stripeCleanupRef.current();
      stripeCleanupRef.current = null;
    }

    const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE;
    if (!stripePublishableKey) {
      dispatch(
        setAlertMessageRequest({
          type: "error",
          message: "Stripe publishable key is not configured.",
        }),
      );
      return;
    }

    const stripeInstance = await loadStripe(stripePublishableKey);
    if (!stripeInstance) return;

    const elements = stripeInstance.elements();

    const elementStyle = {
      base: {
        iconColor: "#000",
        color: "#000",
        fontWeight: 400,
        fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
        fontSize: "16px",
        lineHeight: "30px",
        letterSpacing: "normal",
        fontSmoothing: "antialiased",
        "::placeholder": {
          color: "#9CA3AF",
        },
        ":-webkit-autofill": {
          color: "#777",
        },
      },
      invalid: {
        iconColor: "#F00",
        color: "#F00",
      },
    };

    const cardNumberEl = elements.create("cardNumber", {
      style: elementStyle,
      placeholder: "1234 1234 1234 1234",
    });
    const cardExpiryEl = elements.create("cardExpiry", {
      style: elementStyle,
      placeholder: "06/28",
    });
    const cardCvcEl = elements.create("cardCvc", {
      style: elementStyle,
      placeholder: "369",
    });

    cardNumberEl.mount("#payment-card-number-element");
    cardExpiryEl.mount("#payment-card-expiry-element");
    cardCvcEl.mount("#payment-card-cvc-element");

    let isCardNumberComplete = false;
    let isCardExpiryComplete = false;
    let isCardCvcComplete = false;

    const maybeSetReady = () => {
      if (isCardNumberComplete && isCardExpiryComplete && isCardCvcComplete) {
        setStripe(stripeInstance);
        setCardElement(cardNumberEl as any);
      } else {
        setStripe(null);
        setCardElement(null);
      }
    };

    cardNumberEl.on("change", (event: any) => {
      isCardNumberComplete = !!event.complete;
      if (event.brand) {
        setCardBrand(event.brand);
      }
      maybeSetReady();
    });
    cardExpiryEl.on("change", (event: any) => {
      isCardExpiryComplete = !!event.complete;
      maybeSetReady();
    });
    cardCvcEl.on("change", (event: any) => {
      isCardCvcComplete = !!event.complete;
      maybeSetReady();
    });

    stripeCleanupRef.current = () => {
      try {
        cardNumberEl.unmount();
        cardExpiryEl.unmount();
        cardCvcEl.unmount();
      } catch (e) {}
    };
  };

  useEffect(() => {
    if (stripe && cardElement && cardHolderName.trim().length > 0) {
      setSelectedPaymentInfo({
        cardHolderName,
        stripe,
        cardElement,
      });
    } else if (openCardInputForm) {
      setSelectedPaymentInfo(null);
    }
  }, [cardHolderName, stripe, cardElement, openCardInputForm]);

  const projectTypeLabel =
    selectedProject?.order?.translation_type === "certified"
      ? "certificate"
      : selectedProject?.order?.translation_type || "-";

  const includedAddonKeys = useMemo(() => {
    return new Set(
      includedAddons.flatMap((s) => (s.keys?.length ? s.keys : [s.key])),
    );
  }, [includedAddons]);

  const isServiceIncluded = useCallback(
    (service: ServiceOption): boolean => {
      const keys = service.keys?.length ? service.keys : [service.key];
      return keys.some((k) => includedAddonKeys.has(k));
    },
    [includedAddonKeys],
  );

  const hasActiveTypeAddon = useMemo(() => {
    return typeAddons.some((service) => isServiceIncluded(service));
  }, [typeAddons, isServiceIncluded]);

  const pageCount = Math.max(
    0,
    Number(selectedProject?.order?.page_count || 0) || 0,
  );

  const isCertified = selectedProject?.order?.translation_type === "certified";

  const isMailHardCopyIncluded =
    selectedProject?.order?.include_mail_hard_copy === "1" ||
    selectedProject?.order?.include_2_day_shipping === "1" ||
    includedAddonKeys.has("include_mail_hard_copy") ||
    includedAddonKeys.has("include_2_day_shipping");
  const shippingAddress =
    selectedProject?.order?.address_fields ||
    trackingDetails?.shippingAddress ||
    trackingDetails?.shipping_address ||
    null;
  const trackingNumber =
    trackingDetails?.trackingNumber || trackingDetails?.tracking_number || null;
  const trackingLink =
    trackingDetails?.trackingLink || trackingDetails?.tracking_link || null;
  const shippingStatus =
    trackingDetails?.shippingStatus ||
    trackingDetails?.shipping_status ||
    trackingDetails?.status ||
    null;

  const orderId = useMemo(() => {
    const id = selectedProject?.order?.order_id;
    if (id === undefined || id === null) return null;
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
  }, [selectedProject?.order?.order_id]);

  const openPurchaseModal = (service: ServiceOption) => {
    setSelectedService(service);
    setShippingRecipient("");
    setShippingStreet("");
    setShippingCity("");
    setShippingZip("");
    setShippingState("");
    setShippingCountry("");
    onOpen();
  };

  const isMailHardCopySelected =
    selectedService?.key === "include_mail_hard_copy";
  const isShippingFormValid = useMemo(() => {
    if (!isMailHardCopySelected) return true;
    return (
      shippingRecipient.trim().length > 0 &&
      shippingStreet.trim().length > 0 &&
      shippingCity.trim().length > 0 &&
      shippingZip.trim().length > 0 &&
      shippingState.trim().length > 0 &&
      shippingCountry.trim().length > 0
    );
  }, [
    isMailHardCopySelected,
    shippingRecipient,
    shippingStreet,
    shippingCity,
    shippingZip,
    shippingState,
    shippingCountry,
  ]);

  const isCardSelected = useMemo(() => {
    return (
      !!selectedPaymentInfo ||
      (!!stripe && !!cardElement && cardHolderName.trim().length > 0)
    );
  }, [selectedPaymentInfo, stripe, cardElement, cardHolderName]);

  const isPayButtonDisabled = useMemo(() => {
    if (!isCardSelected) return true;
    if (isMailHardCopySelected && !isShippingFormValid) return true;
    return false;
  }, [isCardSelected, isMailHardCopySelected, isShippingFormValid]);

  const formattedShippingAddressFields = useMemo(() => {
    if (!isMailHardCopySelected) return null;
    return [
      shippingRecipient.trim(),
      shippingStreet.trim(),
      shippingCity.trim(),
      shippingZip.trim(),
      shippingState.trim(),
      shippingCountry.trim(),
    ].join("; ");
  }, [
    isMailHardCopySelected,
    shippingRecipient,
    shippingStreet,
    shippingCity,
    shippingState,
    shippingZip,
    shippingCountry,
  ]);

  const proceedPayment = async (service: ServiceOption, payload: any) => {
    if (!orderId) return;

    try {
      await axiosService.post(
        `/shipping/process-service-payment/${orderId}/${service.id}`,
        payload,
      );

      dispatch(
        setAlertMessageRequest({
          type: "success",
          message: "Service was added to selected Payment.",
        }),
      );

      dispatch(
        updateProjectRequest({
          user_id: user?.user_id,
          order_id: orderId,
          details: {
            ...((service.keys?.length
              ? service.keys.reduce<Record<string, number>>((acc, k) => {
                  acc[k] = 1;
                  return acc;
                }, {})
              : { [service.key]: 1 }) as Record<string, number>),
            ...(service.key === "include_mail_hard_copy" &&
            formattedShippingAddressFields
              ? { address_fields: formattedShippingAddressFields }
              : {}),
          },
        }),
      );

      onRefreshProject();
    } catch (err: any) {
      dispatch(
        setAlertMessageRequest({
          type: "error",
          message: err?.data?.messages?.error || "Error in Processing Payment.",
        }),
      );
    } finally {
      setIsProcessingPayment(false);
      onClose();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const onPayNow = async () => {
    if (!selectedService || !orderId) return;

    if (!isShippingFormValid) {
      dispatch(
        setAlertMessageRequest({
          type: "error",
          message: "Please fill in all shipping details.",
        }),
      );
      return;
    }

    setIsProcessingPayment(true);

    if (uploadProject?.saveCardAsDefault === true && stripe && cardElement) {
      stripe
        .createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: { name: cardHolderName },
        })
        .then(async function (result) {
          const paymentMethodId = result?.paymentMethod?.id;
          if (paymentMethodId && user?.user_id) {
            await axiosService.post(
              `/shipping/put/payment-method/${user.user_id}`,
              {
                payment_method_id: paymentMethodId,
                is_default: 1,
              },
            );
          }
        });
    }

    if (selectedPaymentInfo?.customer_id) {
      const effectiveAmount = getEffectivePrice(selectedService, pageCount);
      const paymentPayload = {
        amount: effectiveAmount,
        customer_id: selectedPaymentInfo.customer_id,
        payment_method_id: selectedPaymentInfo.pm_id,
      };
      await proceedPayment(selectedService, paymentPayload);
      return;
    }

    if (!stripe || !cardElement) {
      dispatch(
        setAlertMessageRequest({
          type: "error",
          message: "Please enter valid card information.",
        }),
      );
      setIsProcessingPayment(false);
      return;
    }

    stripe
      .createToken(cardElement, {
        name: cardHolderName,
      })
      .then(async (res) => {
        const tokenId = res?.token?.id;
        if (tokenId) {
          const effectiveAmount = getEffectivePrice(selectedService, pageCount);
          const paymentPayload = {
            amount: effectiveAmount,
            tokenId,
          };
          await proceedPayment(selectedService, paymentPayload);
        } else {
          dispatch(
            setAlertMessageRequest({
              type: "error",
              message:
                res?.error?.message ||
                "Error in processing the payment. Please contact to support team.",
            }),
          );
          setIsProcessingPayment(false);
        }
      })
      .catch(async (err) => {
        dispatch(
          setAlertMessageRequest({
            type: "error",
            message:
              err?.error?.message ||
              "Error in processing the payment. Please contact to support team.",
          }),
        );
        setIsProcessingPayment(false);
      });
  };

  return (
    <Box bg="#F5F7FA" p="24px" borderRadius="12px" border="1px solid #E6ECF3">
      <Heading fontSize="20px" fontWeight="600" mb="16px" color="#0B0E10">
        Translation project overview
      </Heading>

      {/* Details */}
      <SimpleGrid
        columns={{ base: 1, md: 2 }}
        bg="#fff"
        borderRadius="8px"
        p={{ base: "16px", md: "24px" }}
        pb="16px"
        gap={{ base: "16px", md: "40px" }}
        mb="35px"
      >
        {/* Left column: Order ID, Type, Pages */}
        <Box>
          <Box pb="8px" mb="10px" borderBottom="1px solid #E5E7EB">
            <Flex justify="space-between" align="center">
              <Text fontSize="14px" fontWeight="400" color="#0B0E10">
                Order ID:
              </Text>
              <Text fontSize="14px" fontWeight="400" color="#0B0E10">
                {selectedProject?.order?.order_id}
              </Text>
            </Flex>
          </Box>

          <Box pb="8px" mb="10px" borderBottom="1px solid #E5E7EB">
            <Flex justify="space-between" align="center">
              <Text fontSize="14px" fontWeight="400" color="#0B0E10">
                Type
              </Text>
              <Text
                fontSize="14px"
                fontWeight="400"
                color="#0B0E10"
                textTransform="capitalize"
              >
                {projectTypeLabel}
              </Text>
            </Flex>
          </Box>

          <Box pb="8px" borderBottom="1px solid #E5E7EB">
            <Flex justify="space-between" align="center">
              <Text fontSize="14px" fontWeight="400" color="#0B0E10">
                Pages
              </Text>
              <Text fontSize="14px" fontWeight="400" color="#0B0E10">
                {selectedProject?.order?.page_count || 0}
              </Text>
            </Flex>
          </Box>
        </Box>

        {/* Right column: Project manager, Language, Price */}
        <Box>
          <Box pb="8px" mb="10px" borderBottom="1px solid #E5E7EB">
            <Flex justify="space-between" align="center">
              <Text fontSize="14px" fontWeight="400" color="#0B0E10">
                Project manager
              </Text>
              <Text fontSize="14px" fontWeight="400" color="#0B0E10">
                {selectedProject?.projectManager?.[0]
                  ? `${selectedProject.projectManager[0].first_name} ${selectedProject.projectManager[0].last_name}`
                  : "Not assigned"}
              </Text>
            </Flex>
          </Box>

          <Box pb="8px" mb="10px" borderBottom="1px solid #E5E7EB">
            <Flex justify="space-between" align="center">
              <Text fontSize="14px" fontWeight="400" color="#0B0E10">
                Language:
              </Text>
              <Text fontSize="14px" fontWeight="400" color="#0B0E10">
                {`${selectedProject?.order?.translated_from || "English"} → ${selectedProject?.order?.translated_to || "English"}`}
              </Text>
            </Flex>
          </Box>

          <Box pb="8px" borderBottom="1px solid #E5E7EB">
            <Flex justify="space-between" align="center">
              <Text fontSize="14px" fontWeight="400" color="#0B0E10">
                Price
              </Text>
              <Text fontSize="14px" fontWeight="400" color="#0B0E10">
                ${selectedProject?.order?.order_total || 0}
              </Text>
            </Flex>
          </Box>
        </Box>
      </SimpleGrid>

      {/* Your Attachment */}
      <Box
        mb="16px"
        bg="#FFFFFF"
        borderRadius="12px"
        p="24px"
        lineHeight="113%"
      >
        <Flex align="center" justify="space-between">
          <Text fontSize="14px" fontWeight="400" color="#0B0E10">
            Your attachment
          </Text>
          {hasAttachments ? (
            <Button
              variant="link"
              color="#3A7BFF"
              fontSize="14px"
              fontWeight="600"
              onClick={() => onDownloadFiles("origin")}
            >
              Download original file(s)
            </Button>
          ) : (
            <Text fontSize="14px" fontWeight="400" color="#9CA3AF">
              No attachments
            </Text>
          )}
        </Flex>

        {hasAttachments && (
          <Box mt="14px">
            <Text fontSize="12px" fontWeight="500" color="#58666C" mb="10px">
              Files ({documents.length})
            </Text>
            <Flex direction="column" gap="10px">
              {documents.map((doc: any, idx: number) => {
                const documentId = doc?.document_id || idx;
                const originalName =
                  doc?.origin_file?.document_original_file_name ||
                  doc?.origin_file?.original_file_name ||
                  doc?.origin_file?.file_name ||
                  doc?.document_original_file_name ||
                  doc?.file_name ||
                  `Document ${idx + 1}`;

                const canOpenOrDownload = Boolean(getDocumentFileUrl(doc));
                const translatedName = getTranslatedFileName(doc);
                const canOpenOrDownloadTranslated = hasTranslatedFileForDoc(doc);

                return (
                  <Box key={String(documentId)}>
                    <Flex
                      align="center"
                      justify="space-between"
                      bg="#F8FAFC"
                      border="1px solid #E5E7EB"
                      borderRadius="10px"
                      px="12px"
                      py="10px"
                      gap="12px"
                    >
                      <Box minW={0} flex="1">
                        <Text
                          as="button"
                          type="button"
                          fontSize="14px"
                          fontWeight="500"
                          color="#0B0E10"
                          whiteSpace="nowrap"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          textAlign="left"
                          disabled={!canOpenOrDownload}
                          onClick={() => openDocumentInNewTab(doc)}
                          cursor={canOpenOrDownload ? "pointer" : "not-allowed"}
                        >
                          {originalName}
                        </Text>
                      </Box>

                      <IconButton
                        aria-label="Download"
                        icon={<DownloadIcon />}
                        variant="ghost"
                        size="sm"
                        color="#2F80ED"
                        isDisabled={!canOpenOrDownload}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          downloadDocumentFile(doc);
                        }}
                        _hover={{ bg: "transparent" }}
                        _active={{ bg: "transparent" }}
                        _focusVisible={{ boxShadow: "none" }}
                      />
                    </Flex>

                    {canOpenOrDownloadTranslated && (
                      <Flex
                        align="center"
                        justify="space-between"
                        bg="#EEF5FF"
                        border="1px solid #BFDBFE"
                        borderRadius="10px"
                        px="12px"
                        py="10px"
                        gap="12px"
                        mt="6px"
                      >
                        <Box minW={0} flex="1">
                          <Text
                            fontSize="11px"
                            fontWeight="600"
                            color="#155EEF"
                            textTransform="uppercase"
                            letterSpacing="0.3px"
                            mb="2px"
                          >
                            Translated
                          </Text>
                          <Text
                            as="button"
                            type="button"
                            fontSize="14px"
                            fontWeight="500"
                            color="#0B0E10"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            textAlign="left"
                            onClick={() => downloadTranslatedFile(doc)}
                            cursor="pointer"
                          >
                            {translatedName || "Translated file"}
                          </Text>
                        </Box>

                        <IconButton
                          aria-label="Download translated file"
                          icon={<DownloadIcon />}
                          variant="ghost"
                          size="sm"
                          color="#155EEF"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            downloadTranslatedFile(doc);
                          }}
                          _hover={{ bg: "transparent" }}
                          _active={{ bg: "transparent" }}
                          _focusVisible={{ boxShadow: "none" }}
                        />
                      </Flex>
                    )}
                  </Box>
                );
              })}
            </Flex>
          </Box>
        )}
      </Box>

      {typeAddons.length > 0 && (
        <Box
          mb="16px"
          bg={hasActiveTypeAddon ? "#FFFFFF" : "transparent"}
          borderRadius="12px"
          border="1px solid #3A7BFF"
          p="16px 24px"
        >
          <Text fontSize="16px" fontWeight="600" mb="8px" color="#0B0E10">
            Add-ons
          </Text>
          <SimpleGrid columns={1} gap="12px">
            {typeAddons.map((service) => (
              <Flex
                key={service.id}
                justify="space-between"
                align="center"
                bg="transparent"
                py="12px"
                borderBottom="1px solid #E5E7EB"
              >
                <Box>
                  <Text fontSize="14px" fontWeight="600" color="#0B0E10">
                    {service.addonsName}
                  </Text>
                  {service.addonsDescription && (
                    <Text fontSize="12px" fontWeight="400" color="#505E70">
                      {service.addonsDescription}
                    </Text>
                  )}
                </Box>
                {isServiceIncluded(service) ? (
                  <Flex
                    align="center"
                    justify="center"
                    gap="6px"
                    h="32px"
                    px="16px"
                    maxW="99px"
                    w="99px"
                    borderRadius="100px"
                    border="1px solid #2563EB"
                    bg="transparent"
                  >
                    <IconBlueChecked w="18px" h="18px" />
                    <Text fontSize="14px" fontWeight="500" color="#2563EB">
                      Added
                    </Text>
                  </Flex>
                ) : (
                  <Flex
                    align="center"
                    justify="center"
                    gap="10px"
                    h="32px"
                    px="16px"
                    maxW="99px"
                    w="99px"
                    borderRadius="100px"
                    border="1px solid #2563EB"
                    bg="transparent"
                    color="#2563EB"
                    fontWeight="600"
                    userSelect="none"
                    cursor="pointer"
                    _hover={{ bg: "#EFF6FF" }}
                    onClick={() => openPurchaseModal(service)}
                  >
                    <Flex
                      align="center"
                      justify="center"
                      minW="17px"
                      h="17px"
                      borderRadius="999px"
                      border="1px solid #2563EB"
                      fontSize="12px"
                    >
                      +
                    </Flex>
                    <Text
                      fontSize="14px"
                      fontWeight="600"
                      width="40px"
                      color="#2563EB"
                    >
                      Add
                    </Text>
                  </Flex>
                )}
              </Flex>
            ))}
          </SimpleGrid>

          {isCertified && isMailHardCopyIncluded && (
            <Box bg="white" borderRadius="8px" overflow="hidden">
              <Flex
                justify="space-between"
                align="center"
                px="16px"
                py="12px"
                borderBottom="1px solid #E5E7EB"
              >
                <Text fontSize="14px" color="#0B0E10">
                  Shipping address:
                </Text>
                <Text
                  fontSize="14px"
                  fontWeight="600"
                  color="#0B0E10"
                  textAlign="right"
                  maxW="60%"
                >
                  {shippingAddress || "-"}
                </Text>
              </Flex>
              <Flex
                justify="space-between"
                align="center"
                px="16px"
                py="12px"
                borderBottom="1px solid #E5E7EB"
              >
                <Text fontSize="14px" color="#0B0E10">
                  Tracking number:
                </Text>
                {trackingNumber && trackingLink ? (
                  <Link
                    href={trackingLink}
                    isExternal
                    fontSize="14px"
                    fontWeight="600"
                    color="#2F80ED"
                    textAlign="right"
                    _hover={{ textDecoration: "underline" }}
                  >
                    {trackingNumber}
                  </Link>
                ) : (
                  <Text
                    fontSize="14px"
                    fontWeight="600"
                    color="#0B0E10"
                    textAlign="right"
                  >
                    {trackingNumber || "-"}
                  </Text>
                )}
              </Flex>
              <Flex
                justify="space-between"
                align="center"
                px="16px"
                py="12px"
                borderBottom="1px solid #E5E7EB"
              >
                <Text fontSize="14px" color="#0B0E10">
                  Shipping status
                </Text>
                <Box
                  px="16px"
                  w="84px"
                  h="26px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="999px"
                  border="1px solid #F59E0B"
                  color="#B45309"
                  fontSize="14px"
                  fontWeight="400"
                >
                  {String(shippingStatus || "Pending")
                    .charAt(0)
                    .toUpperCase() +
                    String(shippingStatus || "Pending").slice(1)}
                </Box>
              </Flex>
            </Box>
          )}
        </Box>
      )}

      {/* Download Button */}
      <Button
        w="100%"
        bg="#2F80ED"
        h="48px"
        color="white"
        py="16px"
        borderRadius="24px"
        fontSize="18px"
        fontWeight="400"
        onClick={() => onDownloadFiles("translation")}
        mb="20px"
        _hover={{ bg: "#2B6CB0" }}
        isDisabled={!hasTranslatedFiles}
      >
        Download translated files
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            fontSize="20px"
            lineHeight="24px"
            fontWeight="700"
            color="#000"
            textAlign="center"
            pt="25px"
          >
            Pay for Additional Service
          </ModalHeader>
          <ModalBody py={4}>
            <Box w="100%">
              {isMailHardCopySelected && (
                <Box
                  mb="16px"
                  p="16px"
                  border="1px solid #E6ECF3"
                  borderRadius="12px"
                  bg="#F5F7FA"
                >
                  <Text
                    fontSize="16px"
                    fontWeight="600"
                    color="#0B0E10"
                    mb="16px"
                  >
                    Shipping details
                  </Text>

                  <Flex direction="column" gap="16px">
                    <Input
                      value={shippingRecipient}
                      onChange={(e) => setShippingRecipient(e.target.value)}
                      placeholder="Recipient Name"
                      bg="#FFFFFF"
                      border="1px solid #E6ECF3"
                      borderRadius="12px"
                      fontSize="14px"
                      color="#0B0E10"
                      _hover={{}}
                      _placeholder={{ color: "#505E70" }}
                      _focus={{ borderColor: "#3A7BFF", boxShadow: "none" }}
                      h="48px"
                    />
                    <Input
                      value={shippingStreet}
                      onChange={(e) => setShippingStreet(e.target.value)}
                      placeholder="Street Address"
                      bg="#FFFFFF"
                      border="1px solid #E6ECF3"
                      borderRadius="12px"
                      fontSize="14px"
                      color="#0B0E10"
                      _hover={{}}
                      _placeholder={{ color: "#505E70" }}
                      _focus={{ borderColor: "#3A7BFF", boxShadow: "none" }}
                      h="48px"
                    />
                    <Flex gap={{ base: "16px", md: "12px" }} flexWrap="wrap">
                      <Box flex="1" minW={{ base: "100%", md: "140px" }}>
                        <Input
                          value={shippingCity}
                          onChange={(e) => setShippingCity(e.target.value)}
                          placeholder="City"
                          bg="#FFFFFF"
                          border="1px solid #E6ECF3"
                          borderRadius="12px"
                          fontSize="14px"
                          color="#0B0E10"
                          _hover={{}}
                          _placeholder={{ color: "#505E70" }}
                          _focus={{ borderColor: "#3A7BFF", boxShadow: "none" }}
                          h="48px"
                        />
                      </Box>
                      <Box flex="1" minW={{ base: "100%", md: "140px" }}>
                        <Input
                          value={shippingZip}
                          onChange={(e) => {
                            const val = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 5);
                            setShippingZip(val);
                          }}
                          placeholder="Zip Code"
                          bg="#FFFFFF"
                          border="1px solid #E6ECF3"
                          borderRadius="12px"
                          fontSize="14px"
                          color="#0B0E10"
                          _hover={{}}
                          _placeholder={{ color: "#505E70" }}
                          _focus={{ borderColor: "#3A7BFF", boxShadow: "none" }}
                          h="48px"
                          maxLength={5}
                          inputMode="numeric"
                        />
                      </Box>
                    </Flex>
                    <Input
                      value={shippingState}
                      onChange={(e) => setShippingState(e.target.value)}
                      placeholder="State"
                      bg="#FFFFFF"
                      border="1px solid #E6ECF3"
                      borderRadius="12px"
                      fontSize="14px"
                      color="#0B0E10"
                      _hover={{}}
                      _placeholder={{ color: "#505E70" }}
                      _focus={{ borderColor: "#3A7BFF", boxShadow: "none" }}
                      h="48px"
                    />
                    <Input
                      value={shippingCountry}
                      onChange={(e) => setShippingCountry(e.target.value)}
                      placeholder="Country"
                      bg="#FFFFFF"
                      border="1px solid #E6ECF3"
                      borderRadius="12px"
                      fontSize="14px"
                      color="#0B0E10"
                      _hover={{}}
                      _placeholder={{ color: "#505E70" }}
                      _focus={{ borderColor: "#3A7BFF", boxShadow: "none" }}
                      h="48px"
                    />
                  </Flex>
                </Box>
              )}

              {selectedService && (
                <Box
                  mb="16px"
                  bg="#FFFFFF"
                  border="1px solid #E5E7EB"
                  borderRadius="12px"
                  overflow="hidden"
                >
                  <Box
                    px="16px"
                    py="14px"
                    borderBottom="1px solid #E5E7EB"
                    background="#F5F7FA"
                  >
                    <Text fontSize="16px" fontWeight="600" color="#0B0E10">
                      Order summary
                    </Text>
                  </Box>

                  {selectedService.perPageUsd ? (
                    <>
                      <Flex
                        justify="space-between"
                        align="center"
                        px="16px"
                        py="12px"
                        borderBottom="1px solid #E5E7EB"
                      >
                        <Text fontSize="14px" color="#111827">
                          Pages
                        </Text>
                        <Text fontSize="14px" fontWeight="600" color="#111827">
                          {pageCount}
                        </Text>
                      </Flex>
                      <Flex
                        justify="space-between"
                        align="center"
                        px="16px"
                        py="12px"
                        borderBottom="1px solid #E5E7EB"
                      >
                        <Text fontSize="14px" color="#111827">
                          Rate
                        </Text>
                        <Text fontSize="14px" fontWeight="600" color="#111827">
                          ${selectedService.perPageUsd.toFixed(2)} / page
                        </Text>
                      </Flex>
                    </>
                  ) : null}

                  <Flex
                    justify="space-between"
                    align="center"
                    px="16px"
                    py="12px"
                  >
                    <Text fontSize="14px" fontWeight="600" color="#111827">
                      Total
                    </Text>
                    <Text fontSize="14px" fontWeight="700" color="#2563EB">
                      $
                      {getEffectivePrice(selectedService, pageCount).toFixed(2)}
                    </Text>
                  </Flex>
                </Box>
              )}

              <Box
                p={{ base: "14px", md: "16px" }}
                borderRadius="12px"
                border="1px solid #E5E7EB"
                bg="#FFF"
                background="#F5F7FA"
              >
                <Text
                  fontSize="16px"
                  lineHeight="24px"
                  fontWeight="600"
                  color="#0B0E10"
                  mb="14px"
                >
                  Cards
                </Text>

                {cards?.length > 0 ? (
                  <SavedCardsList
                    cards={cards}
                    selectedCard={selectedPaymentInfo}
                    onSelectCard={(card: any) => {
                      setSelectedPaymentInfo(card);
                      setOpenCardInputForm(false);
                      dispatch(updateProjectStatus({ paymentOption: card }));
                    }}
                    onRemoveCard={(cardId: number) => {
                      if (!user?.user_id) return;
                      dispatch(
                        removeCardInfoRequest({
                          user_id: user.user_id,
                          card_id: cardId,
                        }),
                      );
                    }}
                  />
                ) : (
                  <Text fontSize="14px" color="#667085">
                    No saved cards yet.
                  </Text>
                )}

                <AddNewCardToggle
                  open={openCardInputForm}
                  onToggle={() => setOpenCardInputForm((prev) => !prev)}
                  isDisabled={false}
                />

                <Collapse in={openCardInputForm} animateOpacity>
                  <NewCardForm
                    cardBrand={cardBrand}
                    cardHolderName={cardHolderName}
                    setCardHolderName={setCardHolderName}
                    onFocusStripe={() => {}}
                  />
                </Collapse>
              </Box>
            </Box>
          </ModalBody>
          <ModalFooter borderTop="1px solid #EEE" gap="24px">
            <Button
              bgColor="#FFFFFF"
              color="#3A7BFF"
              h="40px"
              border="1px solid #3A7BFF"
              borderRadius="25px"
              px="24px"
              fontWeight="600"
              _hover={{
                bgColor: "#3A7BFF",
                color: "#FFF",
              }}
              _active={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              onClick={() => {
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              bgColor="#3A7BFF"
              color="#FFFFFF"
              fontSize="16px"
              fontWeight="600"
              px="16px"
              py="12px"
              h="40px"
              borderRadius="25px"
              border="1px solid #3A7BFF"
              isLoading={isProcessingPayment}
              disabled={isPayButtonDisabled}
              _hover={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              _loading={{ pointerEvents: "none" }}
              _active={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              _disabled={{
                backgroundColor: "#E9E9E9 !important",
                borderColor: "#E9E9E9 !important",
                color: "#9CA3AF !important",
              }}
              onClick={() => onPayNow()}
            >
              Pay Now (
              {selectedService
                ? getEffectivePrice(selectedService, pageCount).toFixed(2)
                : "0.00"}
              $)
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
